// TelcoTrack — map overlay: návaznosti spojnice (rozvaděč↔budova / rozvaděč↔rozvaděč / spojkoviště↔rozvaděč).

import { isAcquisitionDrawing } from './drawing.constants';
import { PHASE_CODE, PHASE_COLORS } from './object-workflow/phase-codes';
import {
  isRozvadecZafukMicrotubeKind,
} from './object-workflow/rozvadec-zafuk.constants';
import {
  ALL_TYP_OK_VALUES,
  isAnyTypOkValue,
  TYP_OK_FIBER_COUNT,
} from './object-workflow/typ-ok.constants';
import { hasReachedWorkflowMilestone } from './object-workflow/workflow-model';

/** Stable map overlay id. */
export const NAVAZNOST_MAP_LAYER_ID = 'navaznosti' as const;

/** Czech UI label for the dedicated filter panel. */
export const NAVAZNOST_MAP_LAYER_LABEL = 'Návaznosti';

/** Filter multi-select group (within the Návaznosti panel). */
export const NAVAZNOST_MAP_LAYER_GROUP = 'Návaznosti';

/** Secondary group for Typ OK fiber-count checkboxes. */
export const NAVAZNOST_MAP_LAYER_GROUP_OK = 'OK (počet vláken)';

/** localStorage / SharedPreferences key for enabled category ids (JSON array). */
export const NAVAZNOST_MAP_LAYER_TYPES_PREFS_KEY = 'map_navaznosti_types_v1';

/** @deprecated Prefer {@link NAVAZNOST_MAP_LAYER_TYPES_PREFS_KEY}. */
export const NAVAZNOST_MAP_LAYER_PREFS_KEY = 'map_navaznosti_layer_enabled';

/** Map filter id prefix (`navaznost:ok:2 vl.`, …). */
export const MAP_FILTER_NAVAZNOST_PREFIX = 'navaznost:' as const;

/** @deprecated Single overlay id from the first iteration — migrated to typed categories. */
export const MAP_FILTER_NAVAZNOSTI_ID = `overlay:${NAVAZNOST_MAP_LAYER_ID}` as const;

/**
 * @deprecated Coarse “OK” category from the second iteration — prefs migrate to per-Typ-OK ids.
 */
export const NAVAZNOST_FILTER_OK = 'ok' as const;

/** Realized OK without Typ OK on the link. */
export const NAVAZNOST_FILTER_OK_NEUVEDENO = 'ok:neuvedeno' as const;

/** Realized svazek mikrotrubiček / MT. */
export const NAVAZNOST_FILTER_MT = 'mt' as const;

/** Link exists but not yet zafouknuté. */
export const NAVAZNOST_FILTER_PLANOVANE = 'planovane' as const;

/** Prefix for per-fiber-count OK filter ids (`ok:2 vl.`). */
export const NAVAZNOST_FILTER_OK_PREFIX = 'ok:' as const;

/**
 * Unique Typ OK labels for map filters (shared catalog), ordered by fiber count.
 * Overlaps between budova/rozvaděč catalogs are deduped.
 */
export const NAVAZNOST_OK_TYP_OK_VALUES: readonly string[] = (() => {
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const label of ALL_TYP_OK_VALUES) {
    if (seen.has(label)) continue;
    seen.add(label);
    unique.push(label);
  }
  return unique.sort(
    (a, b) => (TYP_OK_FIBER_COUNT[a] ?? 0) - (TYP_OK_FIBER_COUNT[b] ?? 0),
  );
})();

export function navaznostOkFilterIdFromTypOk(typOk: string | null | undefined): string {
  const trimmed = (typOk ?? '').trim();
  if (trimmed && isAnyTypOkValue(trimmed)) {
    return `${NAVAZNOST_FILTER_OK_PREFIX}${trimmed}`;
  }
  return NAVAZNOST_FILTER_OK_NEUVEDENO;
}

export function navaznostTypOkFromOkFilterId(categoryId: string): string | null {
  if (!categoryId.startsWith(NAVAZNOST_FILTER_OK_PREFIX)) return null;
  if (categoryId === NAVAZNOST_FILTER_OK_NEUVEDENO) return null;
  const typOk = categoryId.slice(NAVAZNOST_FILTER_OK_PREFIX.length);
  return isAnyTypOkValue(typOk) ? typOk : null;
}

/** Per-Typ-OK filter ids (`ok:2 vl.`, …) plus neuvedeno. */
export const NAVAZNOST_OK_FILTER_CATEGORY_IDS: readonly string[] = [
  ...NAVAZNOST_OK_TYP_OK_VALUES.map((typOk) => navaznostOkFilterIdFromTypOk(typOk)),
  NAVAZNOST_FILTER_OK_NEUVEDENO,
];

/**
 * Full filter set for the Návaznosti panel:
 * Typ OK fiber counts → Svazky mikrotrubiček → Plánované.
 */
export const NAVAZNOST_FILTER_CATEGORY_IDS: readonly string[] = [
  ...NAVAZNOST_OK_FILTER_CATEGORY_IDS,
  NAVAZNOST_FILTER_MT,
  NAVAZNOST_FILTER_PLANOVANE,
];

export type NavaznostFilterCategoryId = (typeof NAVAZNOST_FILTER_CATEGORY_IDS)[number];

export function navaznostFilterCategoryLabel(categoryId: string): string {
  if (categoryId === NAVAZNOST_FILTER_MT) return 'Svazky mikrotrubiček';
  if (categoryId === NAVAZNOST_FILTER_PLANOVANE) return 'Plánované';
  if (categoryId === NAVAZNOST_FILTER_OK_NEUVEDENO) return 'OK (neuvedeno)';
  if (categoryId === NAVAZNOST_FILTER_OK) return 'OK';
  const typOk = navaznostTypOkFromOkFilterId(categoryId);
  return typOk ?? categoryId;
}

/** Optický kabel (OK) hotový záfuk — zelená. */
export const NAVAZNOST_CONNECTION_LINE_COLOR_OK = PHASE_COLORS.GREEN;

/** Svazek mikrotrubiček / MT — modrá (i po záfuku). */
export const NAVAZNOST_CONNECTION_LINE_COLOR_MT = PHASE_COLORS.BLUE;

/** Plánované / unknown — šedá. */
export const NAVAZNOST_CONNECTION_LINE_COLOR_FALLBACK = '#64748b';

export const NAVAZNOST_CONNECTION_LINE_COLOR_PLANOVANE = NAVAZNOST_CONNECTION_LINE_COLOR_FALLBACK;

export function isNavaznostOkFilterCategoryId(id: string): boolean {
  return id.startsWith(NAVAZNOST_FILTER_OK_PREFIX) || id === NAVAZNOST_FILTER_OK;
}

export function isNavaznostFilterCategoryId(id: string): id is NavaznostFilterCategoryId {
  return (NAVAZNOST_FILTER_CATEGORY_IDS as readonly string[]).includes(id);
}

export function mapFilterIsNavaznostId(id: string): boolean {
  return id.startsWith(MAP_FILTER_NAVAZNOST_PREFIX);
}

export function mapFilterStripNavaznostPrefix(id: string): string {
  return id.startsWith(MAP_FILTER_NAVAZNOST_PREFIX)
    ? id.slice(MAP_FILTER_NAVAZNOST_PREFIX.length)
    : id;
}

/**
 * Expand legacy coarse `ok` to all per-Typ-OK ids (prefs from prior iteration).
 * Unknown ids are dropped by callers via {@link isNavaznostFilterCategoryId}.
 */
export function expandLegacyNavaznostFilterCategories(
  ids: readonly string[],
): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const id of ids) {
    if (id === NAVAZNOST_FILTER_OK) {
      for (const okId of NAVAZNOST_OK_FILTER_CATEGORY_IDS) {
        if (seen.has(okId)) continue;
        seen.add(okId);
        out.push(okId);
      }
      continue;
    }
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

export function buildNavaznostiFilterSelectedIds(
  enabledCategoryIds: Iterable<string>,
): string[] {
  const enabled = new Set(expandLegacyNavaznostFilterCategories([...enabledCategoryIds]));
  return NAVAZNOST_FILTER_CATEGORY_IDS.filter((id) => enabled.has(id)).map(
    (id) => `${MAP_FILTER_NAVAZNOST_PREFIX}${id}`,
  );
}

/** Dedicated panel: empty selection → all categories OFF. */
export function resolveNavaznostiCategoriesFromFilterIds(
  ids: readonly string[],
): NavaznostFilterCategoryId[] {
  return expandLegacyNavaznostFilterCategories(
    ids.filter(mapFilterIsNavaznostId).map(mapFilterStripNavaznostPrefix),
  ).filter(isNavaznostFilterCategoryId);
}

/**
 * Line color from záfuk kind (`opticka_vlna` → green, svazek/MT → blue).
 * Unset / unknown → fallback gray (never guess OK).
 */
export function resolveNavaznostConnectionLineColor(
  kind: string | null | undefined,
): string {
  if (isRozvadecZafukMicrotubeKind(kind)) return NAVAZNOST_CONNECTION_LINE_COLOR_MT;
  if (kind === 'opticka_vlna') return NAVAZNOST_CONNECTION_LINE_COLOR_OK;
  return NAVAZNOST_CONNECTION_LINE_COLOR_FALLBACK;
}

export function resolveNavaznostConnectionLineColorForCategory(
  category: string,
): string {
  if (category === NAVAZNOST_FILTER_MT) return NAVAZNOST_CONNECTION_LINE_COLOR_MT;
  if (category === NAVAZNOST_FILTER_PLANOVANE) return NAVAZNOST_CONNECTION_LINE_COLOR_PLANOVANE;
  if (isNavaznostOkFilterCategoryId(category)) return NAVAZNOST_CONNECTION_LINE_COLOR_OK;
  return NAVAZNOST_CONNECTION_LINE_COLOR_FALLBACK;
}

/** Category from a záfuk kind + optional Typ OK; unset kind → plánované. */
export function navaznostFilterCategoryFromZafukKind(
  kind: string | null | undefined,
  typOk?: string | null,
): NavaznostFilterCategoryId {
  if (isRozvadecZafukMicrotubeKind(kind)) return NAVAZNOST_FILTER_MT;
  if (kind === 'opticka_vlna') return navaznostOkFilterIdFromTypOk(typOk);
  return NAVAZNOST_FILTER_PLANOVANE;
}

function isBudovaZafouknute(input: {
  status?: string | null;
  zafukLengthM?: number | string | null;
  zafukTypOk?: string | null;
  zafukOkMetrazStartM?: number | string | null;
  zafukOkMetrazKonecM?: number | string | null;
}): boolean {
  if (hasReachedWorkflowMilestone(input.status, PHASE_CODE.BUDOVA_ZAFOUKNUTO)) {
    return true;
  }
  const length = input.zafukLengthM == null || input.zafukLengthM === ''
    ? NaN
    : Number(input.zafukLengthM);
  if (Number.isFinite(length) && length > 0) return true;
  if ((input.zafukTypOk ?? '').trim()) return true;
  const start = input.zafukOkMetrazStartM == null || input.zafukOkMetrazStartM === ''
    ? NaN
    : Number(input.zafukOkMetrazStartM);
  const end = input.zafukOkMetrazKonecM == null || input.zafukOkMetrazKonecM === ''
    ? NaN
    : Number(input.zafukOkMetrazKonecM);
  return Number.isFinite(start) && Number.isFinite(end);
}

/**
 * BUDOVA↔ROZVADEC: zafouknuté → OK:{typOk}; jinak plánované.
 */
export function navaznostFilterCategoryForBudovaLink(input: {
  status?: string | null;
  zafukLengthM?: number | string | null;
  zafukTypOk?: string | null;
  zafukOkMetrazStartM?: number | string | null;
  zafukOkMetrazKonecM?: number | string | null;
}): NavaznostFilterCategoryId {
  if (!isBudovaZafouknute(input)) return NAVAZNOST_FILTER_PLANOVANE;
  return navaznostOkFilterIdFromTypOk(input.zafukTypOk);
}

export function navaznostSegmentMatchesFilter(
  category: string,
  enabled: ReadonlySet<string>,
): boolean {
  if (enabled.has(category)) return true;
  // Legacy coarse `ok` still matches any OK fiber-count category.
  if (enabled.has(NAVAZNOST_FILTER_OK) && isNavaznostOkFilterCategoryId(category)) {
    return true;
  }
  return false;
}

/**
 * Návaznosti lines skip akvizice (same gate as viewport connection rendering).
 * Prefs stay global; toggle UI shows only when this returns true.
 */
export function isNavaznostiVisibleOnDrawing(drawingId: string | null | undefined): boolean {
  return !isAcquisitionDrawing(drawingId);
}
